import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Card,
  CardContent,
  CardActions,
  Box,
  makeStyles,
  Typography,
  IconButton,
  TextField as MuiTextField,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { TextField } from "mui-rff";
import { useTranslate } from "react-polyglot";
import clsx from "clsx";
import CloseIcon from "@material-ui/icons/Close";
import oneCoin from "../../assets/ic_one_coin.png";
import { useBaseData } from "../../Providers/Data/BaseDataProvider.jsx";
import { useAuth } from "../../Providers/Auth/AuthProvider.jsx";
import { useDialogs } from "../../Providers/Common/DialogsProvider.jsx";
import { useProductCatalog } from "../../Providers/Data/ProductCatalogProvider.jsx";
import FlatPrimaryButton from "../../SharedComponents/FlatPrimaryButton.jsx";
import FlatGreyButton from "../../SharedComponents/FlatGreyButton.jsx";
import { useNovaPoshtaApi } from "../../Utils/NovaPoshtaApi.js";
import ReactFinalForm from "../../SharedComponents/ReactFinalForm.jsx";
import { sourceOffer } from "../../constants.js";

const useStyles = makeStyles((theme) => ({
  cardRoot: {
    display: "flex",
    flexDirection: "column",
    minHeight: 350,
    alignItems: "center",
  },
  content: {
    flex: "1 0 auto",
    padding: "12px 20px",
    maxHeight: "70vh",
    overflowY: "auto",
    width: "100%",
    color: "#1B1C1C",
    "& .MuiTextField-root": {
      marginTop: theme.spacing(4),

      backgroundColor: "#FaFbFb",
      "& .MuiOutlinedInput-root": {
        borderRadius: 12,
        border: "2px solid #E2E4E5",
        "&:hover": {
          borderColor: theme.palette.primary.main,
        },
      },
      "& .Mui-focused": {
        borderColor: theme.palette.primary.main,
      },

      "& .MuiInputLabel-outlined": {
        transform: "none",
      },
      "& .MuiInputLabel-formControl": {
        top: -18,
        fontSize: 16,
      },
      "& .MuiFormLabel-root": {
        color: "#54606B",
      },
      "& .MuiFormLabel-root.MuiFocused": {
        color: "#54606B",
      },
      "& .MuiOutlinedInput-notchedOutline": {
        border: "none",
      },
    },
  },
  contentBox: {
    "& p *": {
      overflowWrap: "anywhere",
    },
    overflowWrap: "anywhere",
    padding: "24px 0",
  },
  action: {
    padding: "18px 24px",
    justifyContent: "center",
  },
  closeButton: {
    position: "absolute",
    top: 8,
    right: 8,
  },
  imageBox: {
    width: "25vw",
    height: "auto",
    overflow: "hidden",
  },
  offerImg: {
    width: "100%",
    height: "100%",
    display: "block",
    objectFit: "cover",
    objectPosition: "center",
  },
  title: {
    fontSize: 22,
    fontWeight: 500,
  },
  priceBox: {
    backgroundColor: "#F1F2F2",
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    maxWidth: 400,
    margin: "20px auto",
  },
  coinData: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
    "&:last-child": {
      marginBottom: 0,
    },
  },
  coinText: {
    color: "#49454F",
    fontSize: 18,
  },
  coinBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  helperText: {
    color: "#54606B",
    fontSize: 14,
    marginTop: theme.spacing(1),
  },

  oneCoin: {
    width: 20,
    height: 20,
  },

  contentText: {
    fontSize: 18,
    fontWeight: 500,
    textAlign: "center",
    marginTop: theme.spacing(1),
  },
  coinPrice: {
    color: theme.palette.secondary.yellow,
    marginRight: theme.spacing(0.75),
    fontSize: 18,
    lineHeight: 1,
  },
  orderButtonBottom: {
    minWidth: 200,
    textTransform: "none",
    borderRadius: 22,
  },

  marginTopText: {
    marginTop: theme.spacing(2),
  },
}));

/**
 * @param {?ProductOffer} offer
 * @param {Function} setStep
 * @param {Function} setOpenOrderSuccess - открывает диалог успеха, передавая источник купленного оффера
 * @param {?Function} onClose
 * @returns {*}
 * @constructor
 */
const ProductOrder = ({ offer, setStep, setOpenOrderSuccess, onClose }) => {
  const classes = useStyles();
  const t = useTranslate();
  const { coinBalance, userInfo } = useBaseData();
  const { user } = useAuth();
  const dialogs = useDialogs();
  // Хук работы с API Новой Почты через backend-прокси мобильного API
  const { searchNovaPoshtaCities, getNovaPoshtaWarehouses } = useNovaPoshtaApi();

  // companyId пользователя нужен, чтобы включать логику Новой Почты только для нужной компании
  const companyId = user?.companyId;

  // Флаг, определяющий, нужно ли показывать поля доставки Новой Почты в этой форме
  const isNovaPoshtaDelivery = useMemo(() => companyId === 4472 && offer?.sourceOffer === sourceOffer.merch_offers, [
    companyId,
    offer,
  ]);

  // Локальное состояние для поиска и выбора города Новой Почты
  const [cityQuery, setCityQuery] = useState("");
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [isSearchingCities, setIsSearchingCities] = useState(false);

  // Локальное состояние для выбора отделения Новой Почты
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouseRef, setSelectedWarehouseRef] = useState("");
  const [isLoadingWarehouses, setIsLoadingWarehouses] = useState(false);
  // Флаг выбора типа отделений НП: false - обычные отделения, true - поштоматы (почтоматы)
  const [isPostomatSelected, setIsPostomatSelected] = useState(false);
  const { payOffer } = useProductCatalog();

  /** Вспомогательная функция: находит объект отделения по Ref */
  const findSelectedWarehouse = () => {
    // Здесь мы ищем выбранное отделение по его Ref из массива загруженных отделений
    if (!selectedWarehouseRef) {
      return null;
    }
    return (
      warehouses.find(
        (warehouse) => warehouse.Ref === selectedWarehouseRef || warehouse.ref === selectedWarehouseRef,
      ) || null
    );
  };

  /** Вспомогательная функция: определяем идентификатор города (Ref), аналогично Android-реализации */
  const resolveCityRef = (city) => {
    // backend принимает любой из доступных идентификаторов города, поэтому используем первый непустой
    return (
      city?.Ref ||
      city?.DeliveryCity ||
      city?.SettlementRef ||
      city?.ref ||
      city?.deliveryCity ||
      city?.settlementRef ||
      ""
    );
  };

  // Эффект для дебаунса поиска городов Новой Почты по введённой строке
  useEffect(() => {
    // Если логика Новой Почты не используется, то не делаем запросы
    if (!isNovaPoshtaDelivery) {
      return;
    }

    if (!cityQuery || cityQuery.trim().length < 3) {
      setCities([]);
      return;
    }

    let cancelled = false;

    const handler = setTimeout(async () => {
      setIsSearchingCities(true);
      try {
        const result = await searchNovaPoshtaCities(cityQuery.trim());
        if (!cancelled) {
          setCities(result || []);
        }
      } finally {
        if (!cancelled) {
          setIsSearchingCities(false);
        }
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(handler);
    };
  }, [cityQuery, isNovaPoshtaDelivery]);

  // Эффект подгружает список отделений при изменении выбранного города
  useEffect(() => {
    // Если логика Новой Почты неактивна, не тратим запросы
    if (!isNovaPoshtaDelivery) {
      return;
    }

    const city = selectedCity;
    if (!city) {
      setWarehouses([]);
      setSelectedWarehouseRef("");
      return;
    }

    const cityRef = resolveCityRef(city);
    if (!cityRef) {
      setWarehouses([]);
      setSelectedWarehouseRef("");
      return;
    }

    let cancelled = false;

    /** */
    const loadWarehouses = async () => {
      setIsLoadingWarehouses(true);
      try {
        const result = await getNovaPoshtaWarehouses(cityRef);
        if (!cancelled) {
          setWarehouses(result || []);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingWarehouses(false);
        }
      }
    };

    loadWarehouses().then();

    return () => {
      cancelled = true;
    };
  }, [selectedCity, isNovaPoshtaDelivery]);

  // Отфильтрованный список отделений по типу (отделения / поштоматы), аналогично Android-клиенту
  const filteredWarehouses = useMemo(() => {
    if (!warehouses || warehouses.length === 0) {
      return [];
    }

    /** Определяем, является ли отделение поштоматом по категории или типу */
    const isPostomat = (warehouse) => {
      const categoryRaw = warehouse.CategoryOfWarehouse || warehouse.categoryOfWarehouse || warehouse.category || "";
      const typeRaw = warehouse.TypeOfWarehouse || warehouse.typeOfWarehouse || warehouse.type || "";

      const category = typeof categoryRaw === "string" ? categoryRaw.toLowerCase() : String(categoryRaw).toLowerCase();
      const type = typeof typeRaw === "string" ? typeRaw.toLowerCase() : String(typeRaw).toLowerCase();

      const isPostomatCategory =
        category.includes("почтомат") || category.includes("поштомат") || category.includes("postomat");
      const isPostomatType = type.includes("почтомат") || type.includes("поштомат") || type.includes("postomat");

      return isPostomatCategory || isPostomatType;
    };

    if (isPostomatSelected) {
      // Режим поштоматов: оставляем только почтоматы
      return warehouses.filter((warehouse) => isPostomat(warehouse));
    }

    // Режим обычных отделений: убираем почтоматы из списка
    return warehouses.filter((warehouse) => !isPostomat(warehouse));
  }, [warehouses, isPostomatSelected]);

  // Если оффер отсутствует, компонент не должен ничего рендерить
  if (!offer) {
    return null;
  }

  /** */
  const onCloseCard = () => {
    onClose && onClose();
  };
  /** */
  const onBackStep = () => {
    setStep && setStep("product");
  };

  /** @param {UserDataSubmitForm} data */
  const onSubmit = async (data) => {
    const clone = { ...data };
    if (clone.note === undefined) {
      clone.note = "";
    }
    if (clone.phone === undefined) {
      clone.phone = "";
    }

    // Для заказов с доставкой через Новую Почту для компании 4472 добавляем дополнительные проверки
    if (isNovaPoshtaDelivery) {
      const phone = (clone.phone || "").trim();

      // Валидация телефона: должен состоять из 10 цифр и начинаться с 0, как в Android-клиенте
      const phoneDigits = phone.replace(/\D+/g, "");
      if (!phone || phoneDigits.length !== 10 || !phoneDigits.startsWith("0")) {
        dialogs.error(t("productCatalog.validationPhoneFormat"));
        return;
      }

      // Имя получателя заказа также обязательно, как и в Android-клиенте
      const recipientName = (clone.recipientName || "").trim();
      if (!recipientName) {
        dialogs.error(t("productCatalog.recipientNameRequired"));
        return;
      }
      clone.recipientName = recipientName;

      const city = selectedCity;
      if (!city) {
        // Город доставки обязателен для Новой Почты
        dialogs.error(t("productCatalog.npCityRequired"));
        return;
      }

      const warehouse = findSelectedWarehouse();
      if (!warehouse) {
        // Отделение Новой Почты также обязательно для оформления заказа
        dialogs.error(t("productCatalog.npWarehouseRequired"));
        return;
      }

      const cityRef = resolveCityRef(city);

      // Формируем поля запроса в том же виде, что и Android-клиент
      clone.note = `${city.Present || city.Description || ""}, ${warehouse.Description || ""}`;
      clone.cityName = city.Present || city.Description || "";
      clone.cityRef = cityRef || "";
      clone.warehouseName = warehouse.Description || "";
      clone.warehouseRef = warehouse.Ref || warehouse.ref || "";
    }

    if (!clone.offerId) {
      return;
    }

    await payOffer(clone);
    // Передаём источник оффера, чтобы диалог успеха показал подходящие тексты
    setOpenOrderSuccess(offer.sourceOffer);
    onClose && onClose();
  };

  return (
    <Card className={classes.cardRoot}>
      <ReactFinalForm
        onSubmit={onSubmit}
        initialValues={{
          phone: "",
          note: "",
          // Имя получателя по умолчанию заполняем из профиля пользователя, если оно доступно
          recipientName: userInfo?.name || "",
          sourceOffer: offer.sourceOffer,
          offerId: Number(offer.id.slice(offer.id.lastIndexOf("_") + 1)),
        }}
        // Валидация формы оставлена минимальной: основная логика проверок для Новой Почты перенесена в onSubmit,
        // чтобы иметь доступ к локальному состоянию выбранного города и отделения
        validate={() => {
          const errors = {};
          return errors;
        }}
      >
        <CardContent className={classes.content}>
          <Typography className={classes.contentText}>{t("productCatalog.orderConfirmation")}</Typography>
          <Typography className={classes.title} variant="h6" gutterBottom>
            {offer.name}
          </Typography>
          <Box className={classes.priceBox}>
            <Box className={classes.coinData}>
              <Typography className={classes.coinText}>{t("productCatalog.coinBalance")}</Typography>
              <Box className={classes.coinBox}>
                {/* Баланс монет может быть дробным, отображаем округлённо до целого */}
                <Typography className={classes.coinPrice}>{Math.round(coinBalance)}</Typography>
                <img className={classes.oneCoin} src={oneCoin} alt="oneCoin" />
              </Box>
            </Box>
            <Box className={classes.coinData}>
              <Typography className={classes.coinText}>{t("productCatalog.price")}</Typography>
              <Box className={classes.coinBox}>
                <Typography className={classes.coinPrice}>{offer.price}</Typography>
                <img className={classes.oneCoin} src={oneCoin} alt="oneCoin" />
              </Box>
            </Box>
            <Box className={classes.coinData}>
              <Typography className={classes.coinText}>{t("productCatalog.remaining")}</Typography>
              <Box className={classes.coinBox}>
                {/* Остаток монет после покупки тоже может быть дробным, отображаем округлённо до целого */}
                <Typography className={classes.coinPrice}>{Math.round(coinBalance - offer.price)}</Typography>
                <img className={classes.oneCoin} src={oneCoin} alt="oneCoin" />
              </Box>
            </Box>
          </Box>

          {offer.sourceOffer === sourceOffer.merch_offers && (
            <>
              <Typography className={classes.contentText}>{t("productCatalog.instructionForForm")}</Typography>
              <TextField variant="outlined" label={t("phone")} name="phone" />

              {/* Поле ввода имени получателя заказа для сценария доставки Новой Почты */}
              {isNovaPoshtaDelivery && (
                <TextField variant="outlined" label={t("productCatalog.recipientNameLabel")} name="recipientName" />
              )}

              {isNovaPoshtaDelivery ? (
                <>
                  {/* Поле ввода города Новой Почты на базе стандартного MUI Autocomplete */}
                  <Autocomplete
                    // Разрешаем свободный ввод текста, а варианты берём из API Новой Почты
                    freeSolo
                    options={cities}
                    getOptionLabel={(option) => (option && (option.Present || option.Description)) || ""}
                    loading={isSearchingCities}
                    value={selectedCity}
                    inputValue={cityQuery}
                    onInputChange={(event, newInputValue, reason) => {
                      // Нас интересуют только изменения от пользователя (набор текста/очистка)
                      if (reason === "input" || reason === "clear") {
                        // Пользователь меняет строку поиска города — сбрасываем выбранный город и отделения
                        setCityQuery(newInputValue);
                        setSelectedCity(null);
                        setWarehouses([]);
                        setSelectedWarehouseRef("");
                      }
                    }}
                    onChange={(event, newValue) => {
                      // При выборе города из списка сохраняем объект города и подставляем его название в поле
                      setSelectedCity(newValue || null);
                      if (newValue && newValue.Present) {
                        setCityQuery(newValue.Present);
                      }
                    }}
                    renderInput={(params) => (
                      <MuiTextField
                        {...params}
                        variant="outlined"
                        label={t("productCatalog.npCityLabel")}
                        placeholder={t("productCatalog.npCityPlaceholder")}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        helperText={
                          isSearchingCities
                            ? t("productCatalog.npCitySearching")
                            : cities.length > 0
                            ? t("productCatalog.npCitySelectHint")
                            : t("productCatalog.npCityTypeHint")
                        }
                      />
                    )}
                  />

                  {/* Переключатели типа отделений НП показываем только после выбора города и загрузки отделений */}
                  {selectedCity && warehouses.length > 0 && (
                    <RadioGroup
                      row
                      value={isPostomatSelected ? "postomats" : "departments"}
                      onChange={(event, value) => {
                        const postomat = value === "postomats";
                        // При смене типа отделений сбрасываем выбранное отделение,
                        // чтобы не держать Ref, которого нет в отфильтрованном списке
                        setIsPostomatSelected(postomat);
                        setSelectedWarehouseRef("");
                      }}
                    >
                      <FormControlLabel
                        value="departments"
                        control={<Radio color="primary" />}
                        label={t("productCatalog.npBranchTypeDepartments")}
                      />
                      <FormControlLabel
                        value="postomats"
                        control={<Radio color="primary" />}
                        label={t("productCatalog.npBranchTypePostomats")}
                      />
                    </RadioGroup>
                  )}

                  {/* Автокомплит отделений Новой Почты: варианты берём из уже загруженного массива warehouses (filteredWarehouses), фильтрация по вводу делается на клиенте */}
                  <Autocomplete
                    options={filteredWarehouses}
                    getOptionLabel={(option) => (option && option.Description) || ""}
                    value={findSelectedWarehouse()}
                    onChange={(event, newValue) => {
                      // При выборе отделения сохраняем его Ref, чтобы затем отправить в backend
                      const ref = newValue ? newValue.Ref || newValue.ref || "" : "";
                      setSelectedWarehouseRef(ref);
                    }}
                    disabled={!selectedCity || isLoadingWarehouses}
                    renderInput={(params) => (
                      <MuiTextField
                        {...params}
                        variant="outlined"
                        label={t("productCatalog.npWarehouseLabel")}
                        placeholder={t("productCatalog.npWarehousePlaceholder")}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        helperText={
                          isLoadingWarehouses
                            ? t("productCatalog.npWarehouseLoading")
                            : warehouses.length > 0
                            ? t("productCatalog.npWarehouseSelectHint")
                            : selectedCity
                            ? t("productCatalog.npWarehouseEmpty")
                            : t("productCatalog.npWarehouseCityFirst")
                        }
                      />
                    )}
                  />
                </>
              ) : (
                <>
                  {/* Для остальных компаний сохраняем прежнее поведение с текстовым адресом */}
                  <TextField
                    variant="outlined"
                    placeholder={t("productCatalog.deliveryAddress")}
                    label={t("productCatalog.address")}
                    name="note"
                    InputLabelProps={{ shrink: true }}
                  />
                </>
              )}

              {!isNovaPoshtaDelivery && (
                <Typography className={classes.helperText}>{t("productCatalog.helperText")}</Typography>
              )}
              <Typography className={clsx(classes.helperText, classes.marginTopText)}>
                {t("productCatalog.deliveryOptions")}
              </Typography>
            </>
          )}
        </CardContent>
        <CardActions className={classes.action}>
          <FlatGreyButton className={classes.orderButtonBottom} onClick={onBackStep}>
            {t("back")}
          </FlatGreyButton>
          <FlatPrimaryButton className={classes.orderButtonBottom} type="submit">
            {t("productCatalog.order")}
          </FlatPrimaryButton>
        </CardActions>
      </ReactFinalForm>
      <IconButton aria-label="close" className={classes.closeButton} onClick={onCloseCard}>
        <CloseIcon />
      </IconButton>
    </Card>
  );
};
ProductOrder.propTypes = {
  offer: PropTypes.object.isRequired,
  setStep: PropTypes.func,
  setOpenOrderSuccess: PropTypes.func,
  onClose: PropTypes.func,
};

export default ProductOrder;
